import React from 'react'
import { Avatar, Box, Stack, Typography } from '@mui/material'
import { buildBarLayout } from '../helpers/ganttDates'

import { ROW_HEIGHT } from './GanttTaskList'

export const legendItems = [
  { label: 'Project', color: '#FDF6E2' },
  { label: 'In Progress', color: '#2F80ED' },
  { label: 'Completed', color: '#E0FCE8' },
  { label: 'Overdue', color: '#EB5757' },
  { label: 'Needs Rework', color: '#FFA34D' },
]

const VISIBLE_END_SPACING = 8

export function getColumnWidth(viewMode) {
  return viewMode === 'week' ? 120 : 72
}

export function GanttTimelineHeader({ columns, columnWidth }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns.length}, ${columnWidth}px)`,
        height: 64,
        minWidth: columns.length * columnWidth,
        borderBottom: '1px solid #D0D5DD',
        backgroundColor: '#FFFFFF',
      }}
    >
      {columns.map((column) => (
        <Stack
          key={column.key}
          alignItems="center"
          justifyContent="center"
          spacing={0.25}
          sx={{
            borderLeft: '1px solid #EAECF0',
            boxSizing: 'border-box',
            backgroundColor: column.isToday ? '#EEF4FF' : '#FFFFFF',
            '&:last-of-type': {
              borderRight: '1px solid #EAECF0',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: column.isToday ? 600 : 500,
              color: column.isToday ? '#2563EB' : '#344054',
              lineHeight: 1.1,
            }}
          >
            {column.label}
          </Typography>
          <Typography sx={{ fontSize: 11, color: column.isToday ? '#2563EB' : '#98A2B3' }}>
            {column.subLabel}
          </Typography>
        </Stack>
      ))}
    </Box>
  )
}

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (!parts.length) return '?'
  return `${parts[0]?.[0] || ''}${parts[1]?.[0] || parts[0]?.[1] || ''}`.toUpperCase()
}

const getTaskStatusMarker = (statusKey, accentColor) => {
  if (statusKey === 'completed') {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: accentColor,
          color: '#FFFFFF',
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ✓
      </Box>
    )
  }

  if (statusKey === 'in_progress') {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: accentColor,
          color: '#FFFFFF',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        ◔
      </Box>
    )
  }

  if (statusKey === 'overdue') {
    return <Typography sx={{ color: '#FFFFFF', fontSize: 16, lineHeight: 1 }}>⚠</Typography>
  }

  if (statusKey === 'needs_rework') {
    return <Typography sx={{ color: '#FFFFFF', fontSize: 16, lineHeight: 1 }}>↻</Typography>
  }

  return null
}

export function GanttTimelineCell({
  row,
  columns,
  rangeStart,
  rangeEnd,
  columnWidth,
  safeRightGutterWidth = 0,
}) {
  const layout = buildBarLayout({
    start: row.timelineItem?.start,
    end: row.timelineItem?.end,
    rangeStart,
    rangeEnd,
    columnCount: columns.length,
  })
  const taskLayouts =
    row.rowType === 'task'
      ? (row.timelineItems || [])
          .map((item) => ({
            item,
            layout: buildBarLayout({
              start: item?.start,
              end: item?.end,
              rangeStart,
              rangeEnd,
              columnCount: columns.length,
            }),
          }))
          .filter(({ layout }) => !!layout)
      : []
  const projectLeft =
    row.rowType === 'project' && layout
      ? (layout.gridColumnStart - 1) * columnWidth + (layout.startsBefore ? 0 : 6)
      : 0
  const projectTouchesVisibleEnd =
    row.rowType === 'project' &&
    layout &&
    (layout.endsAfter || layout.gridColumnEnd === columns.length + 1)
  const timelineVisibleWidth = columns.length * columnWidth
  const projectVisibleEndSpacing = projectTouchesVisibleEnd ? safeRightGutterWidth + VISIBLE_END_SPACING : 0
  const projectNaturalWidth =
    row.rowType === 'project' && layout
      ? Math.max(
          24,
          (layout.gridColumnEnd - layout.gridColumnStart) * columnWidth -
            (layout.startsBefore ? 0 : 6) -
            (layout.endsAfter ? 0 : 6) -
            projectVisibleEndSpacing
        )
      : 0
  const projectMaxRight =
    row.rowType === 'project' ? Math.max(24, timelineVisibleWidth - projectVisibleEndSpacing) : 0
  const projectWidth =
    row.rowType === 'project' && layout
      ? Math.max(24, Math.min(projectNaturalWidth, projectMaxRight - projectLeft))
      : 0

  return ( 
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns.length}, ${columnWidth}px)`,
        alignItems: 'center',
        height: '100%',
        minWidth: columns.length * columnWidth,
        position: 'relative',
        backgroundColor: '#FFFFFF',
      }}
    >
      {columns.map((column) => (
        <Box
          key={`${row.id}-${column.key}`}
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
            '&:last-of-type': {
              borderRight: '1px solid transparent',
            },
          }}
        />
      ))}

      {row.rowType === 'project' && layout ? (
        <Box
          sx={{
            position: 'absolute',
            left: `${projectLeft}px`,
            width: `${projectWidth}px`,
            top: `${(ROW_HEIGHT - 44) / 2}px`,
            height: 44,
            backgroundColor: row.timelineItem?.barColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1.5,
            overflow: 'hidden',
            border: `1px solid ${row.timelineItem?.borderColor || row.timelineItem?.accentColor}`,
            boxSizing: 'border-box',
            minWidth: 0,
            zIndex: 1,
            borderTopLeftRadius: layout.startsBefore ? 2 : 10,
            borderBottomLeftRadius: layout.startsBefore ? 2 : 10,
            borderTopRightRadius: layout.endsAfter ? 2 : 10,
            borderBottomRightRadius: layout.endsAfter ? 2 : 10,
            '&::before': layout.startsBefore
              ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '10px',
                  background: 'linear-gradient(90deg, rgba(242, 201, 76, 0.32), rgba(255, 247, 218, 0))',
                  pointerEvents: 'none',
                }
              : undefined,
            '& .project-end-fade':
              projectTouchesVisibleEnd
                ? {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: `${Math.min(24, Math.max(12, safeRightGutterWidth))}px`,
                    background: 'linear-gradient(90deg, rgba(255, 247, 218, 0), rgba(255, 247, 218, 0.92))',
                    pointerEvents: 'none',
                  }
                : undefined,
            '&::after':
              layout.startsBefore || layout.endsAfter
                ? {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: layout.endsAfter ? 6 : 'auto',
                    left: layout.startsBefore ? 6 : 'auto',
                    width: 0,
                    height: 0,
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderLeft: layout.endsAfter ? `6px solid ${row.timelineItem?.textColor || '#344054'}` : 'none',
                    borderRight: layout.startsBefore ? `6px solid ${row.timelineItem?.textColor || '#344054'}` : 'none',
                    opacity: 0.35,
                    pointerEvents: 'none',
                  }
                : undefined,
          }}
        >
          {projectTouchesVisibleEnd ? <Box className="project-end-fade" /> : null}
          {row.rowType === 'project' ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" sx={{ mr: 0.25 }}>
                {(row.timelineItem?.members || []).map((member, index) => (
                  <Avatar
                    key={member?.id ?? `${member?.name}-${index}`}
                    src={member?.avatarUrl || undefined}
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: 10,
                      ml: index === 0 ? 0 : -0.6,
                      border: '2px solid #FFFFFF',
                      boxShadow: '0 0 0 1px rgba(16, 24, 40, 0.05)',
                    }}
                  >
                    {getInitials(member?.name)}
                  </Avatar>
                ))}
              </Stack>
              <Typography
                sx={{ 
                  fontSize: 18, 
                  fontWeight: 500, 
                  color: row.timelineItem?.textColor, 
                  lineHeight: 1 
                }}
                noWrap
              >
                {row.project?.name}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {row.rowType === 'task' && taskLayouts.length ? (
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {taskLayouts.map(({ item, layout: taskLayout }, index) => {
            const taskBarHeight = taskLayouts.length > 1 ? 18 : 42
            const gap = taskLayouts.length > 1 ? 6 : 0
            const contentHeight = taskLayouts.length * taskBarHeight + (taskLayouts.length - 1) * gap
            const topOffset = Math.max(0, (ROW_HEIGHT - contentHeight) / 2) + index * (taskBarHeight + gap)
            const left = (taskLayout.gridColumnStart - 1) * columnWidth + 8
            const touchesVisibleEnd =
              taskLayout.endsAfter || taskLayout.gridColumnEnd === columns.length + 1
            const visibleEndSpacing = touchesVisibleEnd ? safeRightGutterWidth + VISIBLE_END_SPACING : 0
            const naturalWidth = Math.max(
              24,
              (taskLayout.gridColumnEnd - taskLayout.gridColumnStart) * columnWidth -
                16 -
                visibleEndSpacing
            )
            const maxRight = Math.max(24, timelineVisibleWidth - visibleEndSpacing)
            const width = Math.max(24, Math.min(naturalWidth, maxRight - left))

            return (
              <Box
                key={item.id || `${row.id}-${item.itemType}-${index}`}
                sx={{
                  position: 'absolute',
                  left,
                  top: topOffset,
                  width,
                  height: taskBarHeight,
                  borderRadius: item.itemType === 'history' ? 6 : 8,
                  backgroundColor: item.barColor,
                  border: `1px solid ${item.borderColor || item.accentColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  px: taskBarHeight > 18 ? 2 : 1.25,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  opacity: item.itemType === 'history' ? 0.96 : 1,
                  zIndex: item.itemType === 'current' ? 2 : 1,
                  '& .task-end-fade':
                    touchesVisibleEnd
                      ? {
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: `${Math.min(20, Math.max(10, safeRightGutterWidth - 4))}px`,
                          background: `linear-gradient(90deg, rgba(255,255,255,0), ${item.barColor})`,
                          pointerEvents: 'none',
                        }
                      : undefined,
                }}
              >
                {touchesVisibleEnd ? <Box className="task-end-fade" /> : null}
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                  {getTaskStatusMarker(item.statusKey, item.accentColor)}
                  <Typography
                    sx={{
                      fontSize: taskBarHeight > 18 ? 16 : 12,
                      fontWeight: 500,
                      color: item.textColor,
                      lineHeight: 1,
                    }}
                    noWrap
                  >
                    {item.label}
                  </Typography>
                </Stack>
              </Box>
            )
          })}
        </Box>
      ) : null}
    </Box>
  )
}

export function GanttTimelineLegend({ projectCount = null }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ px: 2, py: 1.75, borderTop: '1px solid #EAECF0', flexWrap: 'wrap', backgroundColor: '#FFFFFF' }}
    >
      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap' }}>
        {legendItems.map((item) => (
          <Stack direction="row" alignItems="center" spacing={1.25} key={item.label}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
            <Typography sx={{ fontSize: 14, color: '#344054', fontWeight: 400 }}>{item.label}</Typography>
          </Stack>
        ))}
      </Stack>

      {projectCount != null ? (
        <Typography sx={{ fontSize: 14, color: '#98A2B3' }}>
          Showing {projectCount} of {projectCount} projects
        </Typography>
      ) : null}
    </Stack>
  )
}

export default function GanttTimeline({ columns, rows, rangeStart, rangeEnd, viewMode }) {
  const columnWidth = getColumnWidth(viewMode)

  return (
    <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#FFFFFF' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <GanttTimelineHeader columns={columns} columnWidth={columnWidth} />

        <Box sx={{ minWidth: columns.length * columnWidth }}>
          {rows.map((row) => (
            <GanttTimelineCell
              key={row.id}
              row={row}
              columns={columns}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              columnWidth={columnWidth}
            />
          ))}
        </Box>
      </Box>

      <GanttTimelineLegend />
    </Box>
  )
}
